/**
 * AERO - Help Center
 *
 * Pure matching logic plus the DOM layer for /pages/help.
 * The logic is exported so it can be unit-tested under Node without a DOM;
 * this mirrors how assets/cart-utils.js exposes window.AeroCartUtils.
 *
 * Content lives in snippets/help-questions.liquid. The search index is built
 * by reading that rendered markup, so the questions are written exactly once.
 */
(function () {
  'use strict';

  /* ===== Pure logic (no DOM access below this line) ===== */

  /* Lowercase and strip diacritics so "envio" matches "Envío". */
  function normalize(text) {
    if (text === null || text === undefined) return '';
    return String(text)
      .toLowerCase()
      .normalize('NFD')
      // U+0300-U+036F is the combining-diacritics block. Written escaped on
      // purpose: the literal characters are invisible and corrupt on copy.
      .replace(/[\u0300-\u036f]/g, '');
  }

  function tokenize(query) {
    return normalize(query)
      .split(/\s+/)
      .filter(function (term) {
        return term.length > 0;
      });
  }

  /*
   * Every term must appear somewhere in title+summary. Entries whose title
   * alone satisfies all terms are more relevant, so they come first; within
   * each group the caller's order is preserved.
   */
  function search(entries, query) {
    var terms = tokenize(query);
    if (terms.length === 0) return entries.slice();

    var titleHits = [];
    var bodyHits = [];

    entries.forEach(function (entry) {
      var title = normalize(entry.title);
      var haystack = title + ' ' + normalize(entry.summary);

      var matchesAll = terms.every(function (term) {
        return haystack.indexOf(term) !== -1;
      });
      if (!matchesAll) return;

      var titleMatchesAll = terms.every(function (term) {
        return title.indexOf(term) !== -1;
      });

      if (titleMatchesAll) {
        titleHits.push(entry);
      } else {
        bodyHits.push(entry);
      }
    });

    return titleHits.concat(bodyHits);
  }

  /*
   * Read one parameter out of a query string. Returns '' when absent, so
   * callers never have to distinguish null from empty.
   * Accepts '?q=slug', 'q=slug', and '' alike.
   */
  function paramFromQuery(queryString, name) {
    if (!queryString) return '';
    var raw = String(queryString).replace(/^\?/, '');
    var pairs = raw.split('&');
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      if (eq === -1) continue;
      if (pairs[i].slice(0, eq) !== name) continue;
      try {
        return decodeURIComponent(pairs[i].slice(eq + 1).replace(/\+/g, ' '));
      } catch (err) {
        /* Malformed percent-encoding: treat as absent rather than throwing. */
        return '';
      }
    }
    return '';
  }

  /* Collect unique tags in first-seen order, with a count for each. */
  function tagCounts(entries) {
    var order = [];
    var counts = {};
    entries.forEach(function (entry) {
      if (!entry.tag) return;
      if (!Object.prototype.hasOwnProperty.call(counts, entry.tag)) {
        counts[entry.tag] = 0;
        order.push(entry.tag);
      }
      counts[entry.tag] += 1;
    });
    return order.map(function (tag) {
      return { tag: tag, count: counts[tag] };
    });
  }

  var api = {
    normalize: normalize,
    tokenize: tokenize,
    search: search,
    paramFromQuery: paramFromQuery,
    tagCounts: tagCounts,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.AeroHelpSearch = api;

  /* ===== DOM layer — everything below needs a browser ===== */

  if (typeof document === 'undefined') return;

  /* The strings this file renders itself (the rest is read straight off the
     DOM Liquid already translated) come from data-string-* attributes on
     .aero-help, the same convention assets/cart.js's readStrings() uses on
     .aero-cart-page — so the English defaults below are only a safety net
     for markup that predates the attributes. */
  function readStrings() {
    var node = document.querySelector('.aero-help');
    var data = (node && node.dataset) || {};
    return {
      noResults: data.stringNoResults || 'No results for ',
      resultsHeading: data.stringResultsHeading || 'FREQUENTLY ASKED',
      noResultsHeading: data.stringNoResultsHeading || 'No results',
      resultOne: data.stringResultOne || 'result',
      resultOther: data.stringResultOther || 'results',
      questionOne: data.stringQuestionOne || 'question',
      questionOther: data.stringQuestionOther || 'questions',
    };
  }

  /* "3 results" / "1 result", pluralised with the strings Liquid rendered —
     same shape as assets/cart.js's itemCountLabel. */
  function unitCountLabel(count, unitOne, unitOther) {
    return count + ' ' + (count === 1 ? unitOne : unitOther);
  }

  function init() {
    var strings = readStrings();
    var hub = document.getElementById('helpHub');
    var form = document.getElementById('helpSearchForm');
    var input = document.getElementById('helpSearchInput');
    var list = document.getElementById('helpList');
    var heading = document.getElementById('helpResultsHeading');
    var count = document.getElementById('helpResultsCount');
    var noResults = document.getElementById('helpNoResults');
    var categories = document.getElementById('helpCategories');
    var categoryGrid = document.getElementById('helpCategoryGrid');
    var answerView = document.getElementById('helpAnswerView');
    var answerTitle = document.getElementById('helpAnswerTitle');
    var answerMeta = document.getElementById('helpAnswerMeta');
    var answerBody = document.getElementById('helpAnswerBody');
    var backLink = document.getElementById('helpBackLink');

    if (!hub || !form || !input || !list || !heading || !count || !noResults) return;
    if (!answerView || !answerTitle || !answerMeta || !answerBody || !backLink) return;

    /* Build the index from the rendered questions — single source of truth. */
    var items = Array.prototype.slice.call(list.querySelectorAll('.aero-help__item'));
    var entries = items.map(function (item) {
      var questionEl = item.querySelector('.aero-help__question');
      var answerEl = item.querySelector('.aero-help__answer');
      return {
        slug: item.getAttribute('data-help-slug') || '',
        tag: item.getAttribute('data-help-tag') || '',
        title: questionEl ? questionEl.textContent.trim() : '',
        summary: answerEl ? answerEl.textContent.replace(/\s+/g, ' ').trim() : '',
        answerHtml: answerEl ? answerEl.innerHTML : '',
      };
    });
    if (entries.length === 0) return;

    var itemsBySlug = {};
    items.forEach(function (item) {
      itemsBySlug[item.getAttribute('data-help-slug')] = item;
    });
    var entriesBySlug = {};
    entries.forEach(function (entry) {
      entriesBySlug[entry.slug] = entry;
    });

    var basePath = window.location.pathname;
    var activeTag = '';

    /* ── Category grid ── */

    api.tagCounts(entries).forEach(function (row) {
      var li = document.createElement('li');
      li.className = 'aero-help__category';

      var link = document.createElement('a');
      link.className = 'aero-help__category-link';
      link.href = basePath + '?tag=' + encodeURIComponent(row.tag);

      var name = document.createElement('span');
      name.className = 'aero-help__category-name';
      name.textContent = row.tag;

      var meta = document.createElement('span');
      meta.className = 'aero-help__category-count';
      meta.textContent = unitCountLabel(row.count, strings.questionOne, strings.questionOther);

      link.appendChild(name);
      link.appendChild(meta);
      li.appendChild(link);
      categoryGrid.appendChild(li);

      link.addEventListener('click', function (event) {
        event.preventDefault();
        input.value = '';
        activeTag = row.tag;
        history.pushState({}, '', link.href);
        showHub();
      });
    });
    if (categoryGrid.children.length > 0) categories.hidden = false;

    /* ── Hub rendering ── */

    function renderList(query) {
      var pool = activeTag
        ? entries.filter(function (entry) {
            return entry.tag === activeTag;
          })
        : entries;

      var matches = api.search(pool, query);
      var matchedSlugs = {};
      matches.forEach(function (entry) {
        matchedSlugs[entry.slug] = true;
      });

      items.forEach(function (item) {
        item.hidden = !matchedSlugs[item.getAttribute('data-help-slug')];
      });

      /* Reorder so title matches surface first. appendChild moves in place. */
      matches.forEach(function (entry) {
        var item = itemsBySlug[entry.slug];
        if (item) list.appendChild(item);
      });

      var isFiltering = api.tokenize(query).length > 0;

      if (!isFiltering && !activeTag) {
        heading.textContent = strings.resultsHeading;
        count.textContent = '';
        noResults.hidden = true;
        list.hidden = false;
        return;
      }

      if (matches.length === 0) {
        heading.textContent = strings.noResultsHeading;
        count.textContent = strings.noResults + query;
        noResults.hidden = false;
        list.hidden = true;
        return;
      }

      if (!isFiltering && activeTag) {
        heading.textContent = activeTag.toUpperCase();
      } else {
        heading.textContent = unitCountLabel(matches.length, strings.resultOne, strings.resultOther);
      }
      count.textContent = unitCountLabel(matches.length, strings.resultOne, strings.resultOther);
      noResults.hidden = true;
      list.hidden = false;
    }

    function showHub() {
      answerView.hidden = true;
      hub.hidden = false;
      renderList(input.value);
    }

    function showAnswer(entry) {
      answerTitle.textContent = entry.title;
      answerBody.innerHTML = entry.answerHtml;

      answerMeta.textContent = '';
      if (entry.tag) {
        var tagLink = document.createElement('a');
        tagLink.className = 'aero-help__tag';
        tagLink.href = basePath + '?tag=' + encodeURIComponent(entry.tag);
        tagLink.textContent = entry.tag;
        tagLink.addEventListener('click', function (event) {
          event.preventDefault();
          input.value = '';
          activeTag = entry.tag;
          history.pushState({}, '', tagLink.href);
          showHub();
          window.scrollTo(0, 0);
        });
        answerMeta.appendChild(tagLink);
      }

      hub.hidden = true;
      answerView.hidden = false;
      /* Move focus to the new heading so screen readers and the keyboard
         land on the answer rather than staying where the link used to be. */
      answerTitle.focus();
    }

    /* ── Routing ── */

    function route() {
      var slug = api.paramFromQuery(window.location.search, 'q');
      activeTag = api.paramFromQuery(window.location.search, 'tag');

      if (slug && entriesBySlug[slug]) {
        showAnswer(entriesBySlug[slug]);
        return;
      }

      if (slug) {
        /* Unknown slug: show the hub rather than a blank screen. */
        showHub();
        heading.textContent = 'WE COULDN’T FIND THAT ANSWER';
        return;
      }

      showHub();
    }

    items.forEach(function (item) {
      var link = item.querySelector('.aero-help__link');
      if (!link) return;
      link.addEventListener('click', function (event) {
        /* Let modified clicks open a new tab as the user expects. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        var slug = item.getAttribute('data-help-slug');
        history.pushState({}, '', basePath + '?q=' + encodeURIComponent(slug));
        showAnswer(entriesBySlug[slug]);
        window.scrollTo(0, 0);
      });
    });

    backLink.addEventListener('click', function (event) {
      event.preventDefault();
      history.pushState({}, '', basePath);
      activeTag = '';
      showHub();
      window.scrollTo(0, 0);
    });

    window.addEventListener('popstate', route);

    /* ── Search input ── */

    form.addEventListener('submit', function (event) {
      event.preventDefault();
    });

    var debounceId = null;

    input.addEventListener('input', function () {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(function () {
        renderList(input.value);
      }, 120);
    });

    /* Escape clears the field, matching native search-input expectations. */
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && input.value !== '') {
        input.value = '';
        renderList('');
      }
    });

    route();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
